/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Plus, Edit3, Trash2, Eye, EyeOff, X, Save, Loader2 } from "lucide-react";
import ImageUploader from "@/components/admin/ImageUploader";const EVENT_TYPES = [
  { key: "goodies",    label: "🎁 Goodies / Gift",    color: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-400/30" },
  { key: "award",      label: "🏆 Award",             color: "bg-amber-500/15 text-amber-300 border-amber-400/30" },
  { key: "milestone",  label: "🚀 Milestone",         color: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30" },
  { key: "graduation", label: "🎓 Graduation",        color: "bg-purple-500/15 text-purple-300 border-purple-400/30" },
  { key: "event",      label: "📸 Event / Meetup",    color: "bg-blue-500/15 text-blue-300 border-blue-400/30" },
];

const INPUT = "w-full bg-[#050505] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF0066]";

const EMPTY = {
  image_url: "", caption: "", student_name: "", course_id: "",
  event_type: "goodies", event_date: "", priority: 100, is_published: true,
};

export default function WallOfFamePanel({ courses = [] }) {
  const [data, setData] = useState({ items: [], total: 0, published: 0, drafts: 0 });
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/wall-of-fame");
      setData(d);
    } catch { toast.error("Failed to load Wall of Fame"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const openNew = () => setEdit({ ...EMPTY });

  const save = async () => {
    if (!edit.image_url) { toast.error("Image required"); return; }
    setSaving(true);
    const payload = {
      image_url: edit.image_url,
      caption: edit.caption,
      student_name: edit.student_name || null,
      course_id: edit.course_id || null,
      event_type: edit.event_type,
      event_date: edit.event_date || null,
      priority: Number(edit.priority) || 100,
      is_published: !!edit.is_published,
    };
    try {
      if (edit.id) {
        await api.patch(`/admin/wall-of-fame/${edit.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/admin/wall-of-fame", payload);
        toast.success("Added to Wall of Fame");
      }
      setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (entry) => {
    if (!window.confirm(`Delete this Wall of Fame entry?`)) return;
    try { await api.delete(`/admin/wall-of-fame/${entry.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const togglePublish = async (entry) => {
    try {
      await api.patch(`/admin/wall-of-fame/${entry.id}`, { is_published: !entry.is_published });
      load();
    } catch { toast.error("Update failed"); }
  };

  const filtered = filter === "all"
    ? data.items
    : data.items.filter((i) => i.event_type === filter);

  return (
    <div data-testid="wall-of-fame-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-sm text-[var(--text-dim)]">
            🏆 Wall of Fame — goodies, awards, milestones aur events ki photos. Public {" "}
            <a href="/wall-of-fame" target="_blank" rel="noreferrer" className="text-fuchsia-300 underline">/wall-of-fame</a> page pe show hoti hain + Landing page pe carousel mein.
          </div>
          <div className="text-xs text-[var(--text-mute)] mt-1">
            {data.total} total · {data.published} published · {data.drafts} drafts
          </div>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-1.5 bg-[#FF0066] hover:bg-[#e6005c] text-white text-sm font-semibold rounded-lg px-3 py-2" data-testid="add-wof-btn">
          <Plus className="w-3.5 h-3.5" /> Add photo
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")} count={data.items.length}>All</FilterChip>
        {EVENT_TYPES.map((t) => (
          <FilterChip
            key={t.key}
            active={filter === t.key}
            onClick={() => setFilter(t.key)}
            count={data.items.filter((i) => i.event_type === t.key).length}
            color={t.color}
          >{t.label}</FilterChip>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-10 text-white/40">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-xl p-8 text-center text-white/50">
          <Trophy className="w-10 h-10 mx-auto text-white/20 mb-3" />
          {filter === "all" ? "No Wall of Fame entries yet. Click Add photo to upload your first goodies/award photo." : `No entries in ${filter} category.`}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map((entry) => {
            const eventCfg = EVENT_TYPES.find((e) => e.key === entry.event_type);
            return (
              <div key={entry.id} className="group relative rounded-xl overflow-hidden bg-[#0a0a0a] border border-white/10" data-testid={`wof-card-${entry.id}`}>
                <div className="aspect-square bg-black overflow-hidden">
                  <img src={entry.image_url} alt={entry.caption || "Wall of fame"} className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.opacity = "0.3"; }} />
                </div>
                <div className="p-2.5 space-y-1.5">
                  {eventCfg && <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider border ${eventCfg.color}`}>{eventCfg.label}</span>}
                  {entry.student_name && <div className="text-xs text-white font-semibold truncate">{entry.student_name}</div>}
                  {entry.caption && <div className="text-[11px] text-white/60 line-clamp-2">{entry.caption}</div>}
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-[10px] text-white/40">P{entry.priority}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => togglePublish(entry)} className={`w-6 h-6 rounded inline-flex items-center justify-center border ${entry.is_published ? "bg-emerald-500/10 border-emerald-400/30 text-emerald-300" : "bg-white/5 border-white/15 text-white/40"}`} title={entry.is_published ? "Published — click to unpublish" : "Draft — click to publish"}>
                        {entry.is_published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      </button>
                      <button onClick={() => setEdit({ ...entry })} className="w-6 h-6 rounded bg-white/5 hover:bg-white/10 border border-white/15 inline-flex items-center justify-center" title="Edit"><Edit3 className="w-3 h-3 text-white/70" /></button>
                      <button onClick={() => remove(entry)} className="w-6 h-6 rounded bg-red-500/10 hover:bg-red-500/20 border border-red-400/30 inline-flex items-center justify-center" title="Delete"><Trash2 className="w-3 h-3 text-red-300" /></button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 md:p-6" data-testid="wof-edit-modal">
          <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0a0a0a] border border-[#FF0066]/30 rounded-2xl shadow-2xl">
            <div className="sticky top-0 z-10 bg-[#0a0a0a] border-b border-white/10 px-5 py-3 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>{edit.id ? "Edit photo" : "Add to Wall of Fame"}</h3>
              <button onClick={() => setEdit(null)} className="text-white/50 hover:text-white p-1" aria-label="Close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <Field label="Photo *">
                <ImageUploader
                  value={edit.image_url}
                  onChange={(url) => setEdit({ ...edit, image_url: url })}
                  label="Photo"
                  aspect="1/1"
                  testId="wof-photo"
                />
                <div className="text-[10px] uppercase tracking-wider text-white/40 mt-2">Or paste image URL directly</div>
                <input value={edit.image_url} onChange={(e) => setEdit({ ...edit, image_url: e.target.value })} className={INPUT} placeholder="https://… or /uploads/…" />
              </Field>
              <Field label="Caption (short, 200 chars max)">
                <textarea value={edit.caption} onChange={(e) => setEdit({ ...edit, caption: e.target.value.slice(0, 200) })} className={INPUT + " h-20"} placeholder="e.g. Rahul got Meta Ads certified after 45-day bootcamp!" />
                <div className="text-[10px] text-white/40 mt-1">{(edit.caption || "").length}/200</div>
              </Field>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Student name (optional)">
                  <input value={edit.student_name} onChange={(e) => setEdit({ ...edit, student_name: e.target.value })} className={INPUT} placeholder="Rahul Sharma" />
                </Field>
                <Field label="Event type">
                  <select value={edit.event_type} onChange={(e) => setEdit({ ...edit, event_type: e.target.value })} className={INPUT}>
                    {EVENT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </Field>
                <Field label="Tag course (optional)">
                  <select value={edit.course_id} onChange={(e) => setEdit({ ...edit, course_id: e.target.value })} className={INPUT}>
                    <option value="">— No course tag —</option>
                    {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                  </select>
                </Field>
                <Field label="Event date (optional)">
                  <input type="date" value={edit.event_date ? edit.event_date.slice(0, 10) : ""} onChange={(e) => setEdit({ ...edit, event_date: e.target.value })} className={INPUT} />
                </Field>
                <Field label="Priority (lower = shown first)">
                  <input type="number" value={edit.priority} onChange={(e) => setEdit({ ...edit, priority: e.target.value })} className={INPUT} />
                </Field>
                <Field label="Status">
                  <label className="inline-flex items-center gap-2 text-sm text-white/80 mt-2">
                    <input type="checkbox" checked={!!edit.is_published} onChange={(e) => setEdit({ ...edit, is_published: e.target.checked })} />
                    Published (visible publicly)
                  </label>
                </Field>
              </div>
            </div>
            <div className="sticky bottom-0 bg-[#0a0a0a] border-t border-white/10 px-5 py-3 flex justify-end gap-2">
              <button onClick={() => setEdit(null)} className="px-4 py-2 rounded-full text-sm text-white/70 hover:text-white">Cancel</button>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 bg-[#FF0066] hover:bg-[#e6005c] disabled:opacity-60 text-white text-sm font-semibold rounded-full px-4 py-2" data-testid="wof-save-btn">
                {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-[11px] uppercase tracking-wider text-white/50 font-bold mb-1">{label}</label>
      {children}
    </div>
  );
}

function FilterChip({ active, onClick, count, color, children }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs border transition ${
        active
          ? "bg-[#FF0066] border-[#FF0066] text-white font-semibold"
          : color || "bg-white/5 border-white/15 text-white/70 hover:bg-white/10"
      }`}
    >
      {children}
      <span className={`text-[10px] ${active ? "text-white/90" : "text-white/40"}`}>({count})</span>
    </button>
  );
}
